<script setup lang="ts">
// The assert gate's data checks on the current dataset (status.db.gate) and the violations accepted for this
// environment (spec.db.acceptedViolations). A blocked gate lists each violation with its detail; the owner or an
// operator who may see the data (or an admin) accepts one after review, with a reason, for this dataset only, or
// removes an acceptance. Viewers read it. The API decides; this offers what dataChecks.mayChange allows.
import { computed, ref } from 'vue';
import { must } from '../api/client';
import type { Environment } from '../api/types';
import { useContext } from '../context';
import { bindingText, formatTime, gateBadge, gateCounts, lastGateText, relativeTime } from '../status/status';
import StatusBadge from './StatusBadge.vue';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';

const props = defineProps<{ env: Environment }>();
const emit = defineEmits<{ changed: [] }>();
const { api } = useContext();

const checks = computed(() => props.env.dataChecks);
const gate = computed(() => checks.value?.gate);
const accepted = computed(() => checks.value?.acceptedViolations ?? []);
const may = computed(() => !!checks.value?.mayChange);
const full = computed(() => accepted.value.length >= (checks.value?.maxAccepted ?? 32));

type Key = { assertion: string; subject: string; detail?: string };
const accepting = ref<Key>();
const removing = ref<Key>();
const reason = ref('');
const formError = ref<string>();
const error = ref<unknown>();
const message = ref<string>();
const busy = ref(false);

function startAccept(v: Key) {
  accepting.value = v;
  removing.value = undefined;
  reason.value = '';
  formError.value = undefined;
  error.value = undefined;
  message.value = undefined;
}

function startRemove(v: Key) {
  removing.value = v;
  accepting.value = undefined;
  reason.value = '';
  formError.value = undefined;
  error.value = undefined;
  message.value = undefined;
}

function cancel() {
  accepting.value = undefined;
  removing.value = undefined;
  formError.value = undefined;
}

async function accept() {
  const v = accepting.value;
  if (!v) return;
  const r = reason.value.replace(/\s+/g, ' ').trim();
  if (r.length < 5) {
    formError.value = 'Say why this is harmless in this environment (at least 5 characters); it is recorded with your name.';
    return;
  }
  busy.value = true;
  error.value = undefined;
  try {
    must(await api.POST('/environments/{name}/accepted-violations', {
      params: { path: { name: props.env.name } },
      body: { assertion: v.assertion, subject: v.subject, reason: r },
    }));
    message.value = `Accepted ${v.assertion} ${v.subject}. A blocked gate runs again with it now.`;
    accepting.value = undefined;
    emit('changed');
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
}

async function remove() {
  const v = removing.value;
  if (!v) return;
  busy.value = true;
  error.value = undefined;
  const r = reason.value.replace(/\s+/g, ' ').trim();
  try {
    must(await api.DELETE('/environments/{name}/accepted-violations', {
      params: { path: { name: props.env.name }, query: { assertion: v.assertion, subject: v.subject, ...(r ? { reason: r } : {}) } },
    }));
    message.value = `Removed the acceptance of ${v.assertion} ${v.subject}.`;
    removing.value = undefined;
    emit('changed');
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
}

const isAccepting = (v: Key) => accepting.value?.assertion === v.assertion && accepting.value?.subject === v.subject;
const isRemoving = (v: Key) => removing.value?.assertion === v.assertion && removing.value?.subject === v.subject;
</script>

<template>
  <section class="card" aria-labelledby="datachecks-h" data-testid="data-checks">
    <div class="card-head">
      <h2 id="datachecks-h">Data checks</h2>
      <span class="muted" style="font-size: 13px">The assert gate on the dataset on disk (dataset {{ env.datasetGeneration }}<template v-if="env.db?.backupId">, backup <code>{{ env.db.backupId }}</code></template>).</span>
    </div>

    <template v-if="gate">
      <div class="inline" style="gap: 8px 12px; flex-wrap: wrap" data-testid="gate-summary">
        <StatusBadge :badge="gateBadge(gate)" />
        <span class="muted" style="font-size: 13px">{{ gateCounts(gate) }}</span>
        <span class="muted" style="font-size: 13px" :title="formatTime(gate.ranAt)">ran {{ relativeTime(gate.ranAt) }}<template v-if="gate.job"> · <code>{{ gate.job }}</code></template></span>
      </div>

      <template v-if="gate.violations.length">
        <h3 style="margin-top: 1rem">Violations that block</h3>
        <ul class="vlist" data-testid="gate-violations">
          <li v-for="v in gate.violations" :key="`${v.assertion} ${v.subject}`">
            <div class="grow">
              <div><strong class="mono">{{ v.assertion }}</strong></div>
              <div class="mono" style="overflow-wrap: anywhere">{{ v.subject }}</div>
              <div v-if="v.detail" class="sub">{{ v.detail }}</div>
            </div>
            <button v-if="may && !isAccepting(v)" type="button" class="sm" :disabled="busy || full" data-testid="accept-open" @click="startAccept(v)">Accept…</button>

            <form v-if="isAccepting(v)" role="dialog" aria-modal="false" aria-labelledby="accept-h" novalidate class="accept-form" data-testid="accept-form" @submit.prevent="accept">
              <h4 id="accept-h">Accept this violation for {{ env.name }}?</h4>
              <p class="notice" style="font-size: 13px">
                The gate stops blocking on <code>{{ v.assertion }}</code> for <code>{{ v.subject }}</code> in this environment, for the
                dataset on disk only (dataset {{ env.datasetGeneration }}<template v-if="env.db?.backupId">, backup <code>{{ env.db.backupId }}</code></template>).
                What it found stays in the data<template v-if="v.detail">: {{ v.detail }}</template>. If that is a production host, it stays
                configured in this environment and the environment may send it traffic. Prefer fixing the preparation (sanitize) when it does.
                Your name, the time and the reason are recorded and audited; the next refresh-data removes the acceptance.
              </p>
              <div class="field">
                <label for="accept-reason">Reason <span class="req-label">required</span></label>
                <input id="accept-reason" v-model="reason" type="text" maxlength="500" placeholder="Why it is harmless here, e.g. integration endpoint, no traffic from stage (reviewed with …)" data-testid="accept-reason" />
              </div>
              <p v-if="formError" class="field-error" role="alert">{{ formError }}</p>
              <div class="actions-row">
                <button type="submit" class="primary" :disabled="busy" data-testid="accept-confirm">Accept violation</button>
                <button type="button" @click="cancel">Cancel</button>
              </div>
            </form>
          </li>
        </ul>
        <p v-if="gate.moreViolations" class="hint">… and {{ gate.moreViolations }} more the report had no room for; the gate log lists them all.</p>
        <p v-if="full && may" class="hint">This environment already has {{ accepted.length }} acceptances, the most it may have. Fix the preparation instead.</p>
      </template>

      <template v-if="gate.broken.length">
        <h3 style="margin-top: 1rem">Broken checks</h3>
        <p class="hint">A check that broke blocks the gate and cannot be accepted: the check itself needs fixing (see the gate log).</p>
        <ul data-testid="gate-broken"><li v-for="b in gate.broken" :key="b" class="mono">{{ b }}</li></ul>
        <p v-if="gate.moreBroken" class="hint">… and {{ gate.moreBroken }} more.</p>
      </template>
    </template>
    <p v-else class="muted" data-testid="gate-none">
      <template v-if="env.db?.assertedAt">The gate passed at {{ formatTime(env.db.assertedAt) }}; it ran before the gate reported its results, so no details are recorded.</template>
      <template v-else>No gate result is recorded for this dataset yet.</template>
    </p>

    <h3 style="margin-top: 1.25rem">Accepted violations</h3>
    <p v-if="!accepted.length" class="muted" data-testid="accepted-none">None. Every violation blocks.</p>
    <ul v-else class="vlist" data-testid="accepted-list">
      <li v-for="a in accepted" :key="`${a.assertion} ${a.subject}`">
        <div class="grow">
          <div class="inline" style="gap: 6px; flex-wrap: wrap">
            <strong class="mono">{{ a.assertion }}</strong>
            <span class="pill sm" :class="bindingText(a, env.datasetGeneration).tone" :title="bindingText(a, env.datasetGeneration).title" data-testid="accepted-binding">{{ bindingText(a, env.datasetGeneration).label }}</span>
            <span class="pill sm" :class="lastGateText(a).tone" :title="lastGateText(a).title" data-testid="accepted-last-gate">{{ lastGateText(a).label }}</span>
          </div>
          <div class="mono" style="overflow-wrap: anywhere">{{ a.subject }}</div>
          <div class="sub">“{{ a.reason }}”</div>
          <div class="sub">accepted <template v-if="a.acceptedBy">by {{ a.acceptedBy }} </template><template v-if="a.acceptedAt">{{ formatTime(a.acceptedAt) }}</template><template v-if="!a.acceptedBy && !a.acceptedAt">without a record of who or when (written with kubectl)</template></div>
        </div>
        <button v-if="may && !isRemoving(a)" type="button" class="sm" :disabled="busy" data-testid="remove-open" @click="startRemove(a)">Remove…</button>

        <form v-if="isRemoving(a)" role="dialog" aria-modal="false" aria-labelledby="remove-h" novalidate class="accept-form" data-testid="remove-form" @submit.prevent="remove">
          <h4 id="remove-h">Remove this acceptance?</h4>
          <p class="hint">
            <template v-if="gate?.outcome === 'Blocked'">The gate runs again without it.</template>
            <template v-else>The gate already passed on this dataset: removing it changes nothing on disk until the next refresh-data, which would remove it anyway.</template>
          </p>
          <div class="field">
            <label for="remove-reason">Reason <span class="opt-label">optional</span></label>
            <input id="remove-reason" v-model="reason" type="text" maxlength="500" data-testid="remove-reason" />
          </div>
          <div class="actions-row">
            <button type="submit" class="primary" :disabled="busy" data-testid="remove-confirm">Remove acceptance</button>
            <button type="button" @click="cancel">Cancel</button>
          </div>
        </form>
      </li>
    </ul>

    <p v-if="!may && checks?.mayChangeReason" class="hint" style="margin-top: 12px" data-testid="data-checks-readonly"><Icon name="lock" :size="14" /> {{ checks.mayChangeReason }}</p>
    <p v-if="message" class="info" role="status" style="margin-top: 0.75rem">{{ message }}</p>
    <div v-if="error" style="margin-top: 0.75rem"><ProblemAlert :error="error" context="The API refused the change" /></div>
  </section>
</template>

<style scoped>
.vlist { list-style: none; margin: 0.5rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.vlist > li { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: flex-start; padding: 10px 12px; border: 1px solid var(--line-soft); border-radius: 10px; }
.vlist > li > .grow { flex: 1 1 320px; min-width: 0; }
.accept-form { flex: 1 1 100%; display: flex; flex-direction: column; gap: 0.75rem; padding-top: 0.75rem; border-top: 1px solid var(--line-soft); }
.accept-form h4 { margin: 0; }
</style>
